/**
 * Audible bell used to signal phase ends in countdown / manual_reps workout
 * templates. Web Audio synth (no asset download) — generates a 880Hz sine
 * tone of 0.7s with a 0.3s gap between bells. Default 15 rings ≈ 15s total.
 *
 * Returns a cancel function. `onComplete` fires when all rings have played
 * OR when cancel is invoked, so callers can always transition to the next
 * phase with a single handler.
 */
export function playBell(count: number, onComplete: () => void): () => void {
  let played = 0
  let cancelled = false
  let pendingTimeout: ReturnType<typeof setTimeout> | null = null
  let currentCtx: AudioContext | null = null
  let currentOsc: OscillatorNode | null = null

  const cancel = () => {
    if (cancelled) return
    cancelled = true
    if (pendingTimeout) clearTimeout(pendingTimeout)
    if (currentOsc) try { currentOsc.stop() } catch { /* already stopped */ }
    if (currentCtx) void currentCtx.close()
    onComplete()
  }

  const playOne = () => {
    if (cancelled) return
    try {
      const ctx = new AudioContext()
      currentCtx = ctx
      const osc = ctx.createOscillator()
      currentOsc = osc
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = 'sine'
      osc.frequency.value = 880
      gain.gain.setValueAtTime(0.5, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.7)
      osc.start()
      osc.stop(ctx.currentTime + 0.7)
      osc.onended = () => {
        void ctx.close()
        currentCtx = null
        currentOsc = null
        played++
        if (cancelled) return
        if (played < count) {
          pendingTimeout = setTimeout(playOne, 300)
        } else {
          onComplete()
        }
      }
    } catch {
      // Some browsers (Safari pre-user-gesture) reject AudioContext. Skip
      // sound and resolve normally so the workout flow keeps moving.
      played++
      if (cancelled) return
      if (played < count) {
        pendingTimeout = setTimeout(playOne, 300)
      } else {
        onComplete()
      }
    }
  }

  playOne()
  return cancel
}

/** Default ring count, matching the legacy built-in pages. */
export const DEFAULT_BELL_COUNT = 15
